/**
 * État des filtres du monitoring, porté par l'URL (m-16) : `from`, `to`,
 * `scope_type`, `scope_id` et `tab`. Un lien copié rouvre la même vue.
 *
 * Le préréglage actif n'est pas dans l'URL : il est porté par l'état React
 * (ANOM-3). Sans période dans l'URL, on part des 30 derniers jours, calculés
 * depuis le `today` du SERVEUR (`GET /scopes`, contrat v2.2 §11) dès qu'il est
 * reçu, puis aussitôt écrits dans l'URL. Avant : période en attente (bornes
 * vides, filtres non prêts), aucun calcul avec l'horloge du navigateur.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SCOPE_TYPES, type CommonParams, type ScopeType } from '../../../api/monitoring/v2';
import { DEFAULT_PRESET, computePreset, isIsoDate, type DateRange, type PresetKey } from './periods';

export type MonitoringFilters = {
    scopeType: ScopeType;
    scopeId?: number;
    from: string;
    to: string;
};

export const toCommonParams = (f: MonitoringFilters): CommonParams => ({
    scope_type: f.scopeType,
    ...(f.scopeType !== 'all' && f.scopeId !== undefined ? { scope_id: f.scopeId } : {}),
    from: f.from,
    to: f.to,
});

/**
 * Les filtres sont complets : période connue (dans l'URL ou calculée depuis
 * le `today` du serveur), et périmètre « all » ou périmètre avec son id.
 */
export const filtersReady = (f: MonitoringFilters): boolean =>
    isIsoDate(f.from) && isIsoDate(f.to) && (f.scopeType === 'all' || f.scopeId !== undefined);

/** Période absente : bornes vides tant que le `today` du serveur n'est pas reçu. */
const PENDING: DateRange = { from: '', to: '' };

export const parseFilters = (params: URLSearchParams, fallback: DateRange | null): MonitoringFilters => {
    const rawType = params.get('scope_type');
    const scopeType: ScopeType = (SCOPE_TYPES as readonly string[]).includes(rawType ?? '')
        ? (rawType as ScopeType)
        : 'all';
    const rawId = Number(params.get('scope_id'));
    const scopeId = scopeType !== 'all' && Number.isInteger(rawId) && rawId > 0 ? rawId : undefined;
    const from = params.get('from');
    const to = params.get('to');
    const valid = isIsoDate(from) && isIsoDate(to) && from <= to;
    const period = valid ? { from, to } : fallback ?? PENDING;
    return { scopeType, scopeId, from: period.from, to: period.to };
};

/**
 * `today` : jour courant du fuseau métier renvoyé par `GET /scopes`, `null`
 * tant qu'il n'est pas reçu.
 */
export const useMonitoringFilters = <Tab extends string>(tabs: readonly Tab[], defaultTab: Tab, today: string | null) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Période par défaut : 30 derniers jours depuis le `today` du serveur.
    const fallback = useMemo<DateRange | null>(() => (today ? computePreset(DEFAULT_PRESET, today) : null), [today]);
    const urlHasPeriod = isIsoDate(searchParams.get('from')) && isIsoDate(searchParams.get('to'));
    const [preset, setPreset] = useState<PresetKey | null>(urlHasPeriod ? null : DEFAULT_PRESET);

    const filters = useMemo(() => parseFilters(searchParams, fallback), [searchParams, fallback]);
    const rawTab = searchParams.get('tab');
    const tab: Tab = (tabs as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : defaultTab;

    const write = useCallback(
        (patch: Partial<Record<'scope_type' | 'scope_id' | 'from' | 'to' | 'tab', string | undefined>>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    Object.entries(patch).forEach(([k, v]) => {
                        if (v === undefined || v === '') next.delete(k);
                        else next.set(k, v);
                    });
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // La période par défaut est écrite dans l'URL, dès que `today` est connu,
    // pour que le lien soit partageable.
    useEffect(() => {
        if (!urlHasPeriod && fallback) write({ from: fallback.from, to: fallback.to });
    }, [urlHasPeriod, fallback, write]);

    const setPeriod = useCallback(
        (range: DateRange, nextPreset: PresetKey | null) => {
            setPreset(nextPreset);
            write({ from: range.from, to: range.to });
        },
        [write]
    );

    const setScope = useCallback(
        (scopeType: ScopeType, scopeId?: number) => {
            write({ scope_type: scopeType, scope_id: scopeType === 'all' || scopeId === undefined ? undefined : String(scopeId) });
        },
        [write]
    );

    const setTab = useCallback((next: Tab) => write({ tab: next }), [write]);

    return { filters, preset, tab, setPeriod, setScope, setTab };
};
